import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import * as actions from 'actions'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import {
  parseRequestOptionsFromJSON,
  get as webAuthNGet,
} from '@github/webauthn-json/browser-ponyfill'
import Typography from 'britive-design-system/core/components/typography'
import Spinner from 'britive-design-system/core/components/spinner'
import Button from 'britive-design-system/core/components/button'
import DialogPopup from 'britive-design-system/core/components/dialog'
import toast from 'utils/toast'
import { useLocalStorage } from 'usehooks-ts'
import ProfileList from './ProfileList'
import AccessCredentialsDialog from './components/AccessCredentialsDialog'
import PageFilters from './components/PageFilters'
import StepUpModal from 'components/StepUpModal'
import ProfileGroups from './components/ProfileGroups'
import { stepUpErrorCode } from 'constants/myProfile'
import { searchQueryParam, typeQueryParam, localStorageKey } from './constants'
import { ungroupedLabel } from 'constants/profilesGroupBy'
import { getProfileGroups } from 'utils/profilesGroupBy'
import ApprovalModal from 'components/ApprovalModal'

import {
  getMyResourcesProfiles,
  checkoutMyResourcesProfile,
  checkinMyResourcesProfile,
  getMyResourcesProfileCreds,
  addMyResourcesFavorite,
  deleteMyResourcesFavorite,
  withdrawApprovalRequest,
  getAvailableTemplates,
  fetchAdvancedSettingsForResourceProfiles,
  removeAdvancedSettingsForResourceProfile,
  searchTicketsForResourceProfile,
  removeTicketSearchResultsForResourceProfile,
  findTicketDetailsByIdForResourceProfile,
} from 'action_creators/serverAccess/myResources'
import './index.scss'
import { getProfileData } from './utils'
import { authenticateStepUpMFA } from 'action_creators/user/userMfa'
import { initiateWebAuthnAuthentication } from 'action_creators/user/userWebAuthN'
import { getUserMFAStatus } from 'action_creators/user'
import { mfaFactor } from 'routes/user_settings/constants'

const CSS_CLASSNAME_PREFIX = 'my-resources'

const MyResources = () => {
  const [activeQuickFilter, setActiveQuickFilter] = useLocalStorage(
    localStorageKey.quickFilter
  )
  const [groupByLabels] = useLocalStorage(localStorageKey.groupByLabels, [])

  const [advancedSettingsData, setAdvancedSettingsData] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [showNoMfaDialog, setShowNoMfaDialog] = useState(false)
  const [accessCredsData, setAccessCredsData] = useState(null)
  const [params, setParams] = useState({
    size: 50,
    page: 0,
    type: activeQuickFilter,
  })
  const [stepUpModal, setStepUpModal] = useState({
    visible: false,
    availableMfaModes: [],
  })
  const [profileCreds, setProfileCreds] = useState(null)
  const [groupData, setGroupData] = useState({})
  const [allGroupedProfiles, setAllGroupedProfiles] = useState([])
  const [isExpanded, setIsExpanded] = useState(false)
  const [checkoutData, setCheckoutData] = useState({})

  const dispatch = useDispatch()
  const history = useHistory()
  const profiles = useSelector(state => state.serverAccess.myResources.profiles)
  const advancedSettings = useSelector(
    state => state.serverAccess.myResources.profiles.advancedSettings
  )
  const ticketSearchResults = useSelector(
    state => state.serverAccess.myResources.profiles.ticketSearchResults
  )
  const availableTemplates = useSelector(
    state => state.serverAccess.myResources.availableTemplates
  )

  const resourceLabels = useSelector(state => state.serverAccess.resourceLabels)
  const mfaAuthenticate = useSelector(state => state.userMfa?.authenticate)

  const isFetchingWebAuthNStatus = useSelector(state => state.user?.mfa?.fetching)
  const { data: initiateAuthentication } = useSelector(
    state => state.userWebAuthN?.initiateAuthentication
  )

  useEffect(() => {
    fetchMyResources()
  }, [JSON.stringify(params)])

  useEffect(() => {
    if (!isEmpty(groupByLabels)) {
      handleGroupByFilter(groupByLabels)
    }
  }, [profiles?.data?.length, JSON.stringify(profiles?.params)])

  const resetAccessCreds = () => {
    setAccessCredsData(null)
  }

  const resetProfileCreds = () => {
    setProfileCreds(null)
  }
  const onClose = () => {
    setProfileCreds(null)
    setStepUpModal({
      visible: false,
      availableMfaModes: [],
    })
  }

  useEffect(() => {
    if (mfaAuthenticate?.success) {
      setStepUpModal({
        visible: false,
        availableMfaModes: [],
      })

      checkoutProfile(
        profileCreds?.profileId,
        profileCreds?.resourceId,
        checkoutData
      )
    }
  }, [JSON.stringify(mfaAuthenticate)])

  const validateOtp = otp => {
    dispatch(
      authenticateStepUpMFA(mfaFactor.totp, {
        otp,
      })
    )
  }

  const validateWebAuthn = async () => {
    try {
      const webAuthnInitialResponse = await dispatch(
        initiateWebAuthnAuthentication()
      )

      if (isEmpty(webAuthnInitialResponse?.value?.data)) {
        throw new Error('Cannot complete webauthn authentication.')
      }

      const requestOptions = parseRequestOptionsFromJSON(
        JSON.parse(
          webAuthnInitialResponse?.value?.data?.additionalDetails?.requestOptionsJson
        )
      )

      const publicKeyCredential = await webAuthNGet(requestOptions)
      if (!publicKeyCredential) {
        throw new Error('Cannot complete webauthn authentication.')
      }

      await dispatch(
        authenticateStepUpMFA(mfaFactor.webAuthN, {
          action: 'FINISH_AUTHN',
          requestOptionsId:
            webAuthnInitialResponse.value.data.additionalDetails.requestOptionsId,
          publicKeyCredentialJson: JSON.stringify(publicKeyCredential),
        })
      )
    } catch (error) {
      if (['AbortError', 'NotAllowedError'].includes(error.name)) {
        return null
      }
      toast({
        title: get(
          error,
          'response.data.message',
          'Something went wrong while trying to initiate WebAuthn'
        ),
        type: 'error',
        id: 'my-resources-profile-checkout-webauth-initiate-failure',
      })
    }
  }

  const getRegisteredMFADevices = async () => {
    try {
      const response = await dispatch(getUserMFAStatus(true))
      const mfaData = response?.value?.data || []

      if (mfaData.length === 0) {
        setShowNoMfaDialog(true)
        return
      }

      const getFactorStatus = factor => {
        return mfaData.find(item => item.factor === factor)?.status || 'UNREGISTERED'
      }

      const totpStatus = getFactorStatus(mfaFactor.totp)
      const webauthnStatus = getFactorStatus(mfaFactor.webAuthN)

      if (totpStatus === 'REGISTERED' && webauthnStatus === 'REGISTERED') {
        setStepUpModal({
          visible: true,
          availableMfaModes: [mfaFactor.totp, mfaFactor.webAuthN],
        })
      } else if (totpStatus !== 'REGISTERED' && webauthnStatus === 'REGISTERED') {
        validateWebAuthn()
      } else if (totpStatus === 'REGISTERED' && webauthnStatus !== 'REGISTERED') {
        setStepUpModal({
          visible: true,
          availableMfaModes: [mfaFactor.totp],
        })
      } else {
        setStepUpModal({
          visible: true,
          availableMfaModes: [mfaFactor.webAuthN],
        })
      }
    } catch {
      toast({
        title: 'Unable to checkout the profile.',
        type: 'error',
        id: 'my-resources-profile-checkout-mfa-status-failure',
      })
    }
  }

  const checkoutProfile = async (profileId, resourceId, postBody = {}) => {
    setCheckoutData(postBody)
    dispatch({ type: `${actions.AUTHENTICATE_STEP_UP_MFA}_RESET` })
    setProfileCreds({
      profileId,
      resourceId,
    })
    try {
      setIsLoading(true)
      await dispatch(checkoutMyResourcesProfile(profileId, resourceId, postBody))
      toast({
        title: 'Profile submitted for checkout',
        type: 'success',
        id: 'my-resources-profile-checkout-success',
      })
      resetProfileCreds()
      setCheckoutData({})
    } catch (error) {
      if (error?.response?.data?.errorCode === stepUpErrorCode) {
        getRegisteredMFADevices()
      } else {
        // Throwing the error here for the ApprovalModal component to handle
        throw error
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleApprovalModalSubmit = async values => {
    // eslint-disable-next-line no-useless-catch
    try {
      await checkoutProfile(
        advancedSettingsData.profileId,
        advancedSettingsData.resourceId,
        values
      )
    } catch (error) {
      // Throwing the error here for the ApprovalModal component to handle
      throw error
    }
  }

  const closeAdvanceSettingsModal = () => {
    setAdvancedSettingsData({})
    dispatch(removeAdvancedSettingsForResourceProfile())
    dispatch(removeTicketSearchResultsForResourceProfile())
  }

  const fetchAdvancedSettings = async (profileId, resourceId) =>
    await dispatch(fetchAdvancedSettingsForResourceProfiles(profileId, resourceId))

  const handleCheckoutClick = async profile => {
    const { profileId, resourceId } = profile

    const advancedSettingsResponse = await fetchAdvancedSettings(
      profileId,
      resourceId
    )
    const data = get(advancedSettingsResponse, 'value.data', {})
    const isJustificationRequired = get(
      data,
      'justificationSetting.isJustificationRequiredAtCheckout',
      false
    )
    const isItsmRequired = get(data, 'itsmSetting.isITSMEnabled', false)

    if (!isItsmRequired && !isJustificationRequired) {
      checkoutProfile(profileId, resourceId)
    } else {
      setAdvancedSettingsData(profile)
    }
  }

  const searchTickets = (type, searchText) => {
    dispatch(
      searchTicketsForResourceProfile(
        advancedSettingsData.profileId,
        type,
        searchText
      )
    )
  }

  const findTicket = (type, ticketId) => {
    return dispatch(
      findTicketDetailsByIdForResourceProfile(
        advancedSettingsData.profileId,
        type,
        ticketId
      )
    )
  }

  const checkinProfile = async transactionId => {
    try {
      setIsLoading(true)
      await dispatch(checkinMyResourcesProfile(transactionId))
      toast({
        title: 'Profile submitted for checkin',
        type: 'success',
        id: 'my-resources-profile-checkin-success',
      })
    } catch (error) {
      toast({
        title: get(
          error,
          'response.data.message',
          'Something went wrong while trying to submit checkin'
        ),
        type: 'error',
        id: 'my-resources-profile-checkin-failure',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const fetchAvailableTemplates = async (
    transactionId,
    profileId,
    resourceId,
    isConsole = false
  ) => {
    try {
      setAccessCredsData({
        transactionId,
        profileId,
        resourceId,
        isConsole,
      })
      const response = await dispatch(
        getAvailableTemplates(transactionId, isConsole)
      )

      if (isEmpty(response?.value?.data) && !isConsole) {
        await accessProfileCreds(transactionId, profileId, resourceId)
      }

      if (isConsole && response?.value?.data?.length === 1) {
        await accessProfileCreds(
          transactionId,
          profileId,
          resourceId,
          true,
          response?.value?.data[0]
        )
      }
    } catch (error) {
      toast({
        title: get(
          error,
          'response.data.message',
          'Something went wrong while trying to fetch the templates'
        ),
        type: 'error',
        id: 'my-resources-profile-templates-failure',
      })
    }
  }

  const accessProfileCreds = async (
    transactionId,
    profileId,
    resourceId,
    isConsole = false,
    selectedTemplate
  ) => {
    try {
      setIsLoading(true)
      const credsResponse = await dispatch(
        getMyResourcesProfileCreds(
          transactionId,
          profileId,
          resourceId,
          selectedTemplate
        )
      )

      if (isConsole && credsResponse?.value?.data) {
        if (typeof credsResponse?.value?.data === 'string') {
          window.open(credsResponse?.value?.data, '_blank')
        } else {
          toast({
            title: 'The Console URL is invalid.',
            type: 'error',
            id: `my-resources-profile-credentials-open-link-${selectedTemplate}`,
          })
        }
      }
    } catch (error) {
      toast({
        title: get(
          error,
          'response.data.message',
          'Something went wrong while trying to fetch the profile credentials'
        ),
        type: 'error',
        id: `my-resources-profile-credentials-failure-${selectedTemplate}`,
      })
    } finally {
      setIsLoading(false)
    }
  }

  const withdrawRequest = async (profileId, resourceId) => {
    try {
      setIsLoading(true)
      await dispatch(withdrawApprovalRequest(profileId, resourceId))
      toast({
        title: 'Profile request withdrawn',
        type: 'success',
        id: 'my-resources-profile-withdraw-success',
      })
    } catch (error) {
      toast({
        title: get(
          error,
          'response.data.message',
          'Something went wrong while trying to withdraw request'
        ),
        type: 'error',
        id: 'my-resources-profile-withdraw-failure',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const fetchMyResources = async () => {
    try {
      await dispatch(getMyResourcesProfiles(params))
    } catch (error) {
      toast({
        title: get(
          error,
          'response.data.message',
          'Something went wrong while trying to load profiles'
        ),
        type: 'error',
        id: 'my-resources-profile-search-failure',
      })
    }
  }

  const addFavorite = async (profileId, resourceId) => {
    try {
      setIsLoading(true)
      await dispatch(addMyResourcesFavorite(profileId, resourceId))
    } catch (error) {
      toast({
        title: get(
          error,
          'response.data.message',
          'Something went wrong while trying to add as a favorite'
        ),
        type: 'error',
        id: 'my-resources-profile-add-favorite-failure',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const removeFavorite = async (favoriteId, profileId, resourceId) => {
    try {
      setIsLoading(true)
      await dispatch(deleteMyResourcesFavorite(favoriteId, profileId, resourceId))
    } catch (error) {
      toast({
        title: get(
          error,
          'response.data.message',
          'Something went wrong while trying to remove as a favorite'
        ),
        type: 'error',
        id: 'my-resources-profile-remove-favorite-failure',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const updateParams = (key, value) => {
    const updatedParams = { ...params }
    if (isEmpty(value)) {
      delete updatedParams[key]
    } else {
      updatedParams[key] = value
    }

    // the page should reset on every sort, search and filter change.
    updatedParams.page = 0
    setParams(updatedParams)
  }

  const onSearch = value => {
    updateParams(searchQueryParam, value)
  }

  const handleLoadMore = () => {
    setParams({
      ...params,
      page: params.page + 1,
    })
  }

  const handleFilterButtonClick = clickedType => {
    if (params.type === clickedType) {
      setActiveQuickFilter()
      updateParams(typeQueryParam, null)
      return
    }

    setActiveQuickFilter(clickedType)
    updateParams(typeQueryParam, clickedType)
  }

  const getAllProfileGroups = () => {
    if (isEmpty(profiles?.data)) {
      return []
    }

    const updatedGroups = { ...groupData }

    if (isEmpty(groupData)) {
      // generating an array of index for the profiles data
      const matchedIndexes = Array.from(Array(profiles.data.length).keys())
      updatedGroups[ungroupedLabel] = {
        _matches: matchedIndexes,
        _totalCount: matchedIndexes.length,
      }

      return updatedGroups
    }

    // For Adding Others group
    const ungroupedProfiles = []

    for (
      var curentIndex = 0;
      curentIndex < profiles.data?.length || 0;
      curentIndex++
    ) {
      if (!allGroupedProfiles.includes(curentIndex)) {
        ungroupedProfiles.push(curentIndex)
      }
    }

    if (!isEmpty(ungroupedProfiles)) {
      updatedGroups[ungroupedLabel] = {
        _matches: ungroupedProfiles,
        _totalCount: ungroupedProfiles.length,
      }
    }

    return updatedGroups
  }

  const handleGroupByFilter = selectedItems => {
    if (isEmpty(selectedItems) && isEmpty(groupData)) {
      setGroupData({})
      return
    }

    let clonedLabelItems = []
    let resourceLabelGroups = []

    profiles?.data.forEach((profile, index) => {
      if (!isEmpty(profile.resourceLabels[selectedItems[0]])) {
        clonedLabelItems.push(index)

        resourceLabelGroups = getProfileGroups(
          resourceLabelGroups,
          profile.resourceLabels,
          index,
          selectedItems
        )
      }
    })

    setGroupData(resourceLabelGroups)
    setAllGroupedProfiles(clonedLabelItems)
  }

  const profileListProps = {
    onCheckout: handleCheckoutClick,
    onCheckin: checkinProfile,
    onWithdraw: withdrawRequest,
    onProfileAccess: fetchAvailableTemplates,
    addFavorite,
    removeFavorite,
    fetchAdvancedSettings,
    availableTemplates,
    accessProfileCreds,
  }

  return (
    <>
      <div className="page-header-wrapper">
        <Typography variant="heading5">My Resources</Typography>
      </div>
      <div className="page-content-wrapper">
        {(profiles.loading ||
          isLoading ||
          availableTemplates.loading ||
          resourceLabels.loading ||
          advancedSettings.loading ||
          ticketSearchResults.loading ||
          isFetchingWebAuthNStatus ||
          initiateAuthentication.fetching) && (
          <Spinner size="medium" message={``} overlay />
        )}
        <PageFilters
          activeQuickFilter={params?.type}
          onQuickFilter={handleFilterButtonClick}
          onSearch={onSearch}
          onGroupBy={handleGroupByFilter}
          isExpanded={isExpanded}
          setIsExpanded={setIsExpanded}
        />
        {isEmpty(groupByLabels) ? (
          <ProfileList rows={profiles?.data || []} {...profileListProps} />
        ) : (
          <>
            <br />
            <ProfileGroups
              key={isExpanded}
              groupData={getAllProfileGroups()}
              depth={0}
              isExpanded={isExpanded}
              {...profileListProps}
            />
          </>
        )}
        {isEmpty(profiles?.data) && !profiles.loading && (
          <div className={`${CSS_CLASSNAME_PREFIX}-no-items-message`}>
            <Typography variant="heading6">No Profiles found.</Typography>
          </div>
        )}
        <br />
        <br />
        {profiles.hasMoreData && (
          <div className={`${CSS_CLASSNAME_PREFIX}-load-more`}>
            <Button variant="textOnly" size="medium" onClick={handleLoadMore}>
              Load More ({profiles?.data?.length} of {profiles.totalCount})
            </Button>
          </div>
        )}
        {!availableTemplates.loading &&
          !isEmpty(accessCredsData) &&
          !accessCredsData.isConsole && (
            <AccessCredentialsDialog
              templates={availableTemplates.data}
              credentials={get(
                profiles,
                `credentials[${accessCredsData?.profileId}-${accessCredsData?.resourceId}]`,
                ''
              )}
              showOrigCreds={
                getProfileData(
                  profiles.data,
                  accessCredsData.profileId,
                  accessCredsData.resourceId
                )?.showOrigCreds
              }
              onClose={resetAccessCreds}
              onTemplateSelection={selectedTemplate =>
                accessProfileCreds(
                  accessCredsData.transactionId,
                  accessCredsData.profileId,
                  accessCredsData.resourceId,
                  false,
                  selectedTemplate
                )
              }
            />
          )}
      </div>
      {showNoMfaDialog && (
        <DialogPopup
          type="alert"
          title={'MFA Registration Required'}
          secondaryButtonText={'Close'}
          onCancel={() => setShowNoMfaDialog(false)}
        >
          <Typography variant="body">
            To proceed with Resource checkout, MFA registration is required. Please
            go to
            <div
              className={`${CSS_CLASSNAME_PREFIX}-user-setting-link`}
              onClick={() => history.push('/user-settings')}
            >
              {' '}
              User Settings{' '}
            </div>
            and register an MFA device.
          </Typography>
        </DialogPopup>
      )}
      {stepUpModal.visible && (
        <StepUpModal
          onClose={onClose}
          mfaAuthenticate={mfaAuthenticate}
          availableMfaModes={stepUpModal.availableMfaModes}
          validateOtp={validateOtp}
          validateWebAuthn={validateWebAuthn}
        />
      )}
      {!isEmpty(advancedSettingsData) && (
        <ApprovalModal
          entityName={advancedSettingsData?.resourceName}
          advancedSettings={advancedSettings.data}
          onTicketSearch={searchTickets}
          findTicketDetailsById={findTicket}
          ticketSearchResults={ticketSearchResults.data}
          onSubmit={handleApprovalModalSubmit}
          onModalClose={closeAdvanceSettingsModal}
          action="Access Profile"
          entityType="profile"
        />
      )}
    </>
  )
}

export default MyResources
